import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatusBadge from "@/components/common/StatusBadge";
import { Search, Loader2, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

export default function Games() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ search: "", category: "", status: "" });
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setBusy(true);
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => v && params.set(k, v));
    const { data } = await api.get(`/api/admin/games?${params.toString()}`);
    setItems(data); setBusy(false);
  };
  useEffect(() => { load(); }, [filter]);

  const toggleSel = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  const bulk = async (status) => {
    if (!selected.size) return;
    await api.post("/api/admin/games/bulk-status", { game_ids: [...selected], status });
    toast.success(`Updated ${selected.size} games → ${status}`);
    setSelected(new Set());
    load();
  };

  const cats = ["", "slots", "live", "table", "crash", "instant"];

  return (
    <div className="space-y-6" data-testid="games-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-3 text-[var(--text-3)]"/>
          <input
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            placeholder="Search games…"
            className="w-full pl-9 pr-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded text-sm focus:outline-none focus:border-[var(--gold)]"
            data-testid="games-search"
          />
        </div>
        <select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })}
                className="px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded text-sm focus:outline-none focus:border-[var(--gold)]"
                data-testid="games-category-filter">
          {cats.map(c => <option key={c} value={c}>{c || "All categories"}</option>)}
        </select>
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                className="px-3 py-2 bg-[var(--surface)] border border-[var(--border)] rounded text-sm focus:outline-none focus:border-[var(--gold)]"
                data-testid="games-status-filter">
          <option value="">All statuses</option><option value="active">Active</option><option value="disabled">Disabled</option><option value="maintenance">Maintenance</option>
        </select>
        {selected.size > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-xs text-[var(--text-2)]">{selected.size} selected</span>
            <button onClick={() => bulk("active")} className="btn-ghost px-3 py-1.5 rounded text-xs" data-testid="bulk-enable">Enable</button>
            <button onClick={() => bulk("disabled")} className="btn-ghost px-3 py-1.5 rounded text-xs" data-testid="bulk-disable">Disable</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {busy && <div className="col-span-5 text-[var(--text-2)] flex items-center gap-2"><Loader2 className="animate-spin" size={14}/> Loading…</div>}
        {!busy && items.map(g => {
          const sel = selected.has(g.id);
          return (
            <div key={g.id} className={`card-surface overflow-hidden cursor-pointer fade-up ${sel ? "border-[var(--gold)]" : ""}`}
                 onClick={() => toggleSel(g.id)} data-testid={`game-card-${g.id}`}>
              <div className="aspect-square bg-[var(--surface-3)] relative overflow-hidden">
                <img src={`https://picsum.photos/seed/${g.id}/300/300`} alt={g.game_name} className="w-full h-full object-cover opacity-90"/>
                <div className="absolute top-2 right-2">{sel ? <CheckSquare size={14} className="text-[var(--gold)]"/> : <Square size={14} className="text-white/40"/>}</div>
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/90 to-transparent">
                  <StatusBadge status={g.status}/>
                </div>
              </div>
              <div className="p-3">
                <div className="font-medium text-sm truncate">{g.game_name}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] uppercase tracking-widest text-[var(--text-3)]">{g.category}</span>
                  <span className="text-xs font-mono text-[var(--gold)]">{g.rtp}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
